import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getLastSyncDates, type LexLastSyncDates } from '../services/lexLastSyncService';

interface LexLastSyncContextType {
  dates: LexLastSyncDates;
  refetch: () => Promise<void>;
}

const LexLastSyncContext = createContext<LexLastSyncContextType | undefined>(undefined);

export function LexLastSyncProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [dates, setDates] = useState<LexLastSyncDates>({});

  const refetch = useCallback(async () => {
    try {
      const next = await getLastSyncDates();
      setDates(next);
    } catch {
      setDates({});
    }
  }, []);

  // Charger au login
  useEffect(() => {
    if (user) {
      refetch();
    } else {
      setDates({});
    }
  }, [user, refetch]);

  // Rafraîchir quand l’onglet redevient visible (ex. Vincent revient sur l’onglet après qu’un autre a synchronisé)
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, refetch]);

  // Rafraîchir périodiquement pour que tout le monde voie la même « dernière synchro » sans recharger la page
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => void refetch(), 45_000);
    return () => clearInterval(interval);
  }, [user, refetch]);

  return (
    <LexLastSyncContext.Provider value={{ dates, refetch }}>
      {children}
    </LexLastSyncContext.Provider>
  );
}

export function useLexLastSync(): LexLastSyncContextType {
  const ctx = useContext(LexLastSyncContext);
  if (ctx === undefined) {
    throw new Error('useLexLastSync must be used within LexLastSyncProvider');
  }
  return ctx;
}
